import { getCampaignEdition, getEventFamily } from '@geovelo-frontends/commons';
import { Check } from '@mui/icons-material';
import { Box, Typography } from '@mui/material';
import { ReactNode, useContext } from 'react';
import { useTranslation } from 'react-i18next';

import { AppContext } from '../../app/context';
import bttfLogo from '../../assets/img/bttf/logo-bttf.svg';
import bttfIllustration from '../../assets/img/bttf/onboarding-illus-bttf.png';
import illus from '../../assets/img/onboarding-illus.svg';

function Steps({
  items,
  currentStepIndex,
  eventCode,
  completedKeys,
}: {
  completedKeys?: string[];
  currentStepIndex: number;
  eventCode?: string;
  items: Array<{ key: string; optional?: boolean } & ({ label: ReactNode } | { labelKey: string })>;
}): React.JSX.Element {
  const {
    app: { currentEvent },
  } = useContext(AppContext);
  const version = eventCode ?? currentEvent?.code?.toLowerCase();
  const { t } = useTranslation();
  const edition = getCampaignEdition(version);

  if (edition?.stepperImage) {
    return (
      <Box position="relative" sx={{ height: '100svh', flexShrink: 0 }}>
        <Box
          component="img"
          src={edition.stepperImage}
          sx={{ height: '100%', width: 'auto', display: 'block' }}
        />
        <Box
          display="flex"
          flexDirection="column"
          gap={3}
          justifyContent="center"
          paddingX={{ xs: 5, lg: 8 }}
          paddingY={5}
          position="absolute"
          sx={{ inset: 0, zIndex: 1 }}
        >
          {items.map(({ key, optional, ...otherProps }, index) => (
            <Item
              currentStepIndex={currentStepIndex}
              done={completedKeys?.includes(key)}
              index={index}
              key={key}
              label={'labelKey' in otherProps ? t(otherProps.labelKey) : otherProps.label}
              optional={optional}
              version={version}
            />
          ))}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="center"
      minHeight="max(100svh, 100%)"
      position="relative"
      width="100%"
    >
      {version === 'bttf' && (
        <Box flexShrink={0} paddingTop={5} paddingX={{ xs: 5, lg: 8 }}>
          <img height={80} src={bttfLogo} />
        </Box>
      )}
      <Box
        display="flex"
        flexDirection="column"
        flexGrow={1}
        gap={3}
        justifyContent="center"
        paddingX={{ xs: 5, lg: 8 }}
        paddingY={5}
      >
        {items.map(({ key, optional, ...otherProps }, index) => (
          <Item
            currentStepIndex={currentStepIndex}
            done={completedKeys?.includes(key)}
            index={index}
            key={key}
            label={'labelKey' in otherProps ? t(otherProps.labelKey) : otherProps.label}
            optional={optional}
            version={version}
          />
        ))}
      </Box>
      <Box
        flexShrink={0}
        height={200}
        sx={{
          backgroundImage: `url(${version === 'bttf' ? bttfIllustration : illus})`,
          backgroundPosition: version === 'bttf' ? 'bottom right' : 'bottom left',
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'contain',
        }}
      />
    </Box>
  );
}

function Item({
  currentStepIndex,
  done,
  index,
  optional,
  label,
  version,
}: {
  currentStepIndex: number;
  done?: boolean;
  index: number;
  label: ReactNode;
  optional?: boolean;
  version: string | undefined;
}): React.JSX.Element {
  const accentColor = getEventFamily(version) ? '#223B5B' : '#fff';
  const checked = done || currentStepIndex > index;

  return (
    <Box
      alignItems="center"
      display="flex"
      gap={2}
      sx={{ opacity: currentStepIndex !== index ? 0.5 : 1 }}
    >
      <Box
        alignItems="center"
        border={`1px solid ${accentColor}`}
        borderRadius={4}
        color={accentColor}
        display="flex"
        height={32}
        justifyContent="center"
        width={32}
      >
        {checked ? (
          <Check sx={{ height: 16, width: 16 }} />
        ) : (
          <Typography color={accentColor}>{index + 1}</Typography>
        )}
      </Box>
      <Typography color={accentColor}>
        {label}
        {optional ? (
          <>
            {' '}
            <Typography component="i" variant="caption">
              (facultatif)
            </Typography>
          </>
        ) : (
          <></>
        )}
      </Typography>
    </Box>
  );
}

export default Steps;
